import Cookies from 'js-cookie';

import type { PreferredLanguage } from 'app/types/language';

export const PREFERRED_LANGUAGE_KEY = 'com.vistracks.vtlib.PREFERRED_LANGUAGE';
export const PREFERRED_LANGUAGE_COOKIE = 'preferredLanguage';

export const I18N_LOCALE_BY_API: Record<PreferredLanguage, string> = {
  en: 'en-US',
  fr: 'fr-FR',
  es: 'es-ES',
};

/** Short code for API and localStorage (en | fr | es). */
export function toApiLanguage(value: string): PreferredLanguage {
  const normalized = value.trim();
  if (
    normalized === 'en' ||
    normalized === 'en-US' ||
    normalized === 'English (United States)'
  ) {
    return 'en';
  }
  if (
    normalized === 'fr' ||
    normalized === 'fr-FR' ||
    normalized === 'Français (Canada)'
  ) {
    return 'fr';
  }
  if (
    normalized === 'es' ||
    normalized === 'es-ES' ||
    normalized === 'Español (Estados Unidos)'
  ) {
    return 'es';
  }
  const base = normalized.split(/[-_]/)[0]?.toLowerCase();
  if (base === 'fr' || base === 'es') return base;
  return 'en';
}

/** Locale for i18n dropdowns (en-US | fr-FR | es-ES). */
export function toI18nLocale(value: string): string {
  return I18N_LOCALE_BY_API[toApiLanguage(value)] ?? 'en-US';
}

export type LanguagePreferenceSources = {
  apiLanguage?: string | null;
  storedLanguage?: string | null;
  i18nLanguage?: string | null;
};

/**
 * Compare API, localStorage, and i18n language values after normalizing
 * to short API codes. Login/i18n selection is the active choice.
 */
export function resolveLanguagePreferenceSync(
  sources: LanguagePreferenceSources,
): {
  selectedCode: PreferredLanguage;
  apiCode: PreferredLanguage | null;
  storedCode: PreferredLanguage;
  i18nCode: PreferredLanguage;
  isApiInSync: boolean;
  isStorageInSync: boolean;
} {
  const i18nCode = toApiLanguage(
    sources.i18nLanguage || sources.storedLanguage || 'en',
  );
  const storedCode = toApiLanguage(
    sources.storedLanguage || sources.i18nLanguage || 'en',
  );
  const apiCode = sources.apiLanguage
    ? toApiLanguage(sources.apiLanguage)
    : null;

  // At login, the language the user currently has in i18n wins.
  const selectedCode = i18nCode;

  return {
    selectedCode,
    apiCode,
    storedCode,
    i18nCode,
    isApiInSync: apiCode === selectedCode,
    isStorageInSync: storedCode === selectedCode,
  };
}

/**
 * Persist language for the API the same way CanJS does (`Cookies.set("preferredLanguage", ...)`).
 * Axios already sends cookies via `withCredentials: true` — no request header is needed.
 */
export function setPreferredLanguageCookie(languageCode: PreferredLanguage) {
  Cookies.set(PREFERRED_LANGUAGE_COOKIE, languageCode, {
    path: '/',
    secure: true,
    sameSite: 'None',
  });
}

export function applyLanguageToClient(
  languageCode: PreferredLanguage,
  changeLanguage: (lng: string) => void | Promise<unknown>,
) {
  localStorage.setItem('i18nextLng', languageCode);
  setPreferredLanguageCookie(languageCode);
  return changeLanguage(languageCode);
}
